import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Button, Form, FormInstance } from 'antd'
import styled from 'styled-components'
import { useMedplum } from '@medplum/react'
import { XClose } from 'public/assets/icons'
import IconButton from 'components/Common/IconButton/IconButton'
import { ClinicalNoteTypes, GeneralNoteTypes } from 'fhir/Communication/constants'
import { CLINICAL_NOTE_TYPE_MAP, GENERAL_NOTE_TYPE_MAP } from 'utils/staticLists/Notes/notes'
import { useStore } from 'store'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NotificationContext } from 'contexts/NotificationContext'
import PatientContext from 'contexts/PatientContext'
import NoteFormItem from './NoteFormItem'
import { clinicalNoteFields, commonFields, generalNoteFields } from './noteFields'
import useCreateClinicalNote from '../../hooks/useCreateNote'

const NOTE_FORM_EXCLUDE_FIELD = ['patientId']

interface NoteTypesFormContainerProps {
  form: FormInstance
  closePopupForm: VoidFunction
  source: 'navbar' | 'assessment'
  loadPatientNotesData?: VoidFunction
}

const NoteTypesFormContainer: FC<NoteTypesFormContainerProps> = ({
  form,
  closePopupForm,
  source,
  loadPatientNotesData
}) => {
  const { notify } = useContext(NotificationContext)
  const { patientId: contextPatientId } = useContext(PatientContext)

  const medplum = useMedplum()
  const { patientDataQueries: useApi } = useFeatureFlags()
  const { getPatient } = useStore((state) => state.patient)

  const {
    createHospitalizationNote,
    createMedicalProcedureNote,
    createERVisitNote,
    createInfectionNote,
    createInjuryNote,
    createCareCoordinationNote,
    createGeneralNote,
    createGrievanceComplaintNote,
    createFallNote
  } = useCreateClinicalNote(source)
  const [formType, setFormType] = useState('')
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const createClinicalNoteCompletionHandlerMap = {
    [ClinicalNoteTypes.HOSPITALIZATION]: createHospitalizationNote,
    [ClinicalNoteTypes.MEDICAL_PROCEDURE]: createMedicalProcedureNote,
    [ClinicalNoteTypes.ER_VISIT]: createERVisitNote,
    [ClinicalNoteTypes.INFECTION]: createInfectionNote,
    [ClinicalNoteTypes.INJURY]: createInjuryNote,
    [ClinicalNoteTypes.CARE_COORDINATION]: createCareCoordinationNote,
    [ClinicalNoteTypes.FALL]: createFallNote,
    [GeneralNoteTypes.GENERAL]: createGeneralNote,
    [GeneralNoteTypes.GRIEVANCE_COMPLAINT]: createGrievanceComplaintNote
  }

  const createGeneralNoteCompletionHandlerMap = {
    [GeneralNoteTypes.GENERAL]: createGeneralNote,
    [GeneralNoteTypes.GRIEVANCE_COMPLAINT]: createGrievanceComplaintNote
  }

  const onFormFinish = async (): Promise<void> => {
    setIsCreatingNote(true)
    try {
      const values = await form.validateFields()
      if (values) {
        // Use contextPatientId when creating notes from within an assessment
        const patientId = values.patientId || contextPatientId

        if (!patientId) {
          throw new Error('Patient ID is missing. Please ensure you have selected a valid patient.')
        }

        const patient = await getPatient(medplum, patientId, undefined, useApi)

        if (!patient) {
          throw new Error('Patient not found. Please ensure you have selected a valid patient.')
        }

        const patientState = patient?.addressState

        if (!patientState) {
          throw new Error(
            'Patient state is missing. Please update the patient chart with a valid state.'
          )
        }

        values.patientState = patientState
        const result = await {
          ...createClinicalNoteCompletionHandlerMap,
          ...createGeneralNoteCompletionHandlerMap
        }[formType](values)
        if (result) {
          loadPatientNotesData && loadPatientNotesData()
          closePopupForm()
          setFormType('')
          form.resetFields()
        }
      }
    } catch (error) {
      notify.error({
        message: 'Error',
        description: error.message
      })
    } finally {
      setIsCreatingNote(false)
    }
  }

  const isWithinAssessment = source === 'assessment'

  const resetFormFields = useCallback((): void => {
    const fields = form.getFieldsValue()
    Object.keys(fields).forEach((key) => {
      if (!NOTE_FORM_EXCLUDE_FIELD.includes(key)) {
        form.setFieldValue(key, undefined)
      }
    })
  }, [form])

  const NoteTypeFormField = useMemo((): React.ReactElement => {
    const noteList = isWithinAssessment
      ? CLINICAL_NOTE_TYPE_MAP
      : { ...CLINICAL_NOTE_TYPE_MAP, ...GENERAL_NOTE_TYPE_MAP }
    const options = Object.keys(noteList).map((type) => ({
      label: noteList[type].label,
      value: type
    }))
    return NoteFormItem({
      ...commonFields.noteType,
      options,
      onChange: (e) => {
        resetFormFields()
        form.setFieldValue('noteType', e)
        setFormType(e)
      }
    })
  }, [form, isWithinAssessment, resetFormFields])

  const SearchablePatientFormField = useMemo(
    (): React.ReactElement => NoteFormItem(commonFields.searchablePatient),
    []
  )

  // Reset form type on unmount
  useEffect(() => setFormType(''), [])

  return (
    <>
      <IconButton
        type="ghost"
        icon={<XClose style={{ width: 20 }} />}
        onClick={() => closePopupForm()}
        style={{ position: 'absolute', top: '8px', right: '8px' }}
      >
        {''}
      </IconButton>
      <br />
      {!isWithinAssessment && SearchablePatientFormField}
      {NoteTypeFormField}
      {formType &&
        { ...clinicalNoteFields, ...generalNoteFields }[formType]?.map((inputData) => {
          let CurrentComponent: React.ReactElement | null = null
          if (Array.isArray(inputData)) {
            CurrentComponent = (
              <NoteFormSection>
                {inputData.map((data) => {
                  const CurrentComponent = NoteFormItem(data, data.name)
                  return CurrentComponent
                })}
              </NoteFormSection>
            )
          } else {
            CurrentComponent = NoteFormItem(inputData, inputData.name)
          }
          return CurrentComponent
        })}

      <Form.Item wrapperCol={{ offset: 13, span: 16 }} style={{ marginTop: '30px' }}>
        <Button
          style={{ marginRight: '10px' }}
          onClick={() => closePopupForm()}
          disabled={isCreatingNote}
        >
          Cancel
        </Button>
        <Button
          style={{ margin: 0 }}
          onClick={onFormFinish}
          loading={isCreatingNote}
          disabled={isCreatingNote}
        >
          Create Note
        </Button>
      </Form.Item>
    </>
  )
}

const NoteFormSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`

export default NoteTypesFormContainer
